/**
 * Custom-input-dropdown which used for stylesed dropdown-components
 */
import { LitElement, css, customElement, html } from 'lit-element';

/**
 * LitElement
 * A simple base class for creating fast, lightweight web components.
 *
 * @docs https://lit-element.polymer-project.org/
 */
@customElement('custom-input-dropdown')
class CustomInputDropdownComponent extends LitElement {
  static get properties() {
    return {
      field_name: { type: String },
      field_values: { type: Array },
      selectedValue: { type: Object },
      content_key: { type: Object },
      isOpened: { type: Boolean },
      isError: { type: Boolean },
      show_label: { type: Boolean },
      color: { type: String },
      closest_element: { type: String },
      closest_element_class: { type: Number },
      disabled: { type: Object },
      disabled_var: { type: String },
      disabled_static: { type: Boolean },
    };
  }

  constructor() {
    super();
    this.field_values = [];
    this.field_name = '';
    this.show_label = false;
    this.selectedValue = {
      value: null,
      content_key: '',
    };
    this.content_key = {
      label: '',
      placeholder: '',
    };
    this.isOpened = false;
    this.isError = false;
    this.color = '';
    this.closest_element = '.accordion-collapse-info';
    this.closest_element_class = 'z-5';
    this.disabled = false;
    this.disabled_var = '';

    this.fieldValidationEvent = (kasko) => this.fieldValidation(kasko);
    this.onDisabledVarChanged = this.onDisabledVarChanged.bind(this);
    this.onDisabledChanged = this.onDisabledChanged.bind(this);
    this.onOutsideClick = this.onOutsideClick.bind(this);
  }

  static get styles() {
    return css`
      .dropdown {
        position: relative;
        width: 100%;
      }

      .dropdown-label {
        font-size: 13px;
      }

      .dropdown-field {
        background-color: #FFFFFF;
        border: 1px solid #D2D2D2;
        border-radius: 8px;
        height: 48px;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 15px;
        background-size: auto !important;
        background-repeat: no-repeat;
        background-position-y: center;
        background-position-x: 95%;
        background-image: url("data:image/svg+xml,%0A%3Csvg width='24px' height='14px' viewBox='0 0 24 14' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cg id='Symbols' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Icon-small/Arrow-down' transform='translate(0.000000, -4.000000)' fill='%23433894'%3E%3Cpolygon id='Path' points='1.90178571 4.66964286 0.669642857 5.90178571 11.3839286 16.6160714 12 17.2053571 12.6160714 16.6160714 23.3303571 5.90178571 22.0982143 4.66964286 12 14.7678571'%3E%3C/polygon%3E%3C/g%3E%3C/g%3E%3C/svg%3E") !important;
      }

      .dropdown-field[filled] {
        color: #000;
        font-family: "Eina 03 Semi Bold", 'Eina 03', Helvetica, Arial, sans-serif;
      }

      .orange-dropdown.dropdown-field {
        background-image: url("data:image/svg+xml,%0A%3Csvg width='24px' height='14px' viewBox='0 0 24 14' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cg id='Symbols' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Icon-small/Arrow-down' transform='translate(0.000000, -4.000000)' fill='%23FB8900'%3E%3Cpolygon id='Path' points='1.90178571 4.66964286 0.669642857 5.90178571 11.3839286 16.6160714 12 17.2053571 12.6160714 16.6160714 23.3303571 5.90178571 22.0982143 4.66964286 12 14.7678571'%3E%3C/polygon%3E%3C/g%3E%3C/g%3E%3C/svg%3E") !important;
      }

      .red-dropdown.dropdown-field {
        background-image: url("data:image/svg+xml,%0A%3Csvg width='24px' height='14px' viewBox='0 0 24 14' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cg id='Symbols' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Icon-small/Arrow-down' transform='translate(0.000000, -4.000000)' fill='%23F05D23'%3E%3Cpolygon id='Path' points='1.90178571 4.66964286 0.669642857 5.90178571 11.3839286 16.6160714 12 17.2053571 12.6160714 16.6160714 23.3303571 5.90178571 22.0982143 4.66964286 12 14.7678571'%3E%3C/polygon%3E%3C/g%3E%3C/g%3E%3C/svg%3E") !important;
      }

      .black-dropdown.dropdown-field {
        background-image: url("data:image/svg+xml,%0A%3Csvg width='24px' height='14px' viewBox='0 0 24 14' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cg id='Symbols' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Icon-small/Arrow-down' transform='translate(0.000000, -4.000000)' fill='%23000'%3E%3Cpolygon id='Path' points='1.90178571 4.66964286 0.669642857 5.90178571 11.3839286 16.6160714 12 17.2053571 12.6160714 16.6160714 23.3303571 5.90178571 22.0982143 4.66964286 12 14.7678571'%3E%3C/polygon%3E%3C/g%3E%3C/g%3E%3C/svg%3E") !important;
      }

      .gray-dropdown.dropdown-field {
        color: #AFAFAF;
        background-image: url("data:image/svg+xml,%0A%3Csvg width='24px' height='14px' viewBox='0 0 24 14' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cg id='Symbols' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Icon-small/Arrow-down' transform='translate(0.000000, -4.000000)' fill='%23AFAFAF'%3E%3Cpolygon id='Path' points='1.90178571 4.66964286 0.669642857 5.90178571 11.3839286 16.6160714 12 17.2053571 12.6160714 16.6160714 23.3303571 5.90178571 22.0982143 4.66964286 12 14.7678571'%3E%3C/polygon%3E%3C/g%3E%3C/g%3E%3C/svg%3E") !important;
      }

      [active].dropdown-field {
        border: 1px solid #000;
        color: #000;
      }

      [error].dropdown-field {
        border: 1px solid #E30613;
        color: #000;
      }

      .dropdown-field span {
        padding: 0 0 0 14px;
        max-width: calc(100% - 48px);
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
      }

      .dropdown-options {
        position: absolute;
        width: 100%;
        background: #fff;
        z-index: 12;
        max-height: 180px;
        overflow: scroll;
      }

      .dropdown-options ul {
        padding: 0;
        margin: 0;
        list-style-type: none;
        font-size: 15px;
      }

      .dropdown-options li {
        color: #787772;
        padding: 11px 14px;
        border-bottom: 1px solid #EFE6DC;
        cursor: pointer;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .dropdown-options li:hover {
        background: #F7F7F7;
        color: #787772;
      }

      .dropdown-options li:last-child {
        border-bottom: none;
      }

      .dropdown-options li[selected] {
        color: #000000;
        background: #F7F7F7;
      }

      @media only screen and (max-width: 576px) {
        .dropdown-field span {
          font-size: 14px;
        }
      }
    `;
  }

  render() {
    this.selectedValue = this.selectedValue || {};
    const formField = this.kasko.getFormState()[this.field_name];
    const isValidContentKey =
      formField !== null &&
      formField !== undefined &&
      formField !== '' &&
      !!this.selectedValue.content_key;
    const fieldContent = isValidContentKey
      ? this.kasko.getTranslation(this.selectedValue.content_key)
      : this.kasko.getTranslation(this.content_key.placeholder);

    return html`
      <div class="dropdown" id="${this.field_name}">
        ${
          this.show_label
            ? html`<span
          class="dropdown-label">${this.kasko.getTranslation(this.content_key.label)}</span>`
            : ''
        }
          <div class="dropdown-field ${this.disabled_static ? 'gray' : this.color}-dropdown"
          ?active="${this.isOpened}"
             ?error="${this.isError}"
             ?filled="${this.selectedValue.value !== null && this.selectedValue.value !== undefined}"
             @click=${() => this.toggleDropdown()}>
             <span>${fieldContent}</span>
        </div>
        ${
          this.isOpened
            ? html`
          <div class="dropdown-options">
            <ul>
              ${this.field_values.map(
                (item) => html`
                <li ?selected="${item.value === this.selectedValue.value}"
                    @click=${() => this.selectItem(item)}>${this.kasko.getTranslation(
                  item.content_key,
                )}
                </li>
              `,
              )}
            </ul>
          </div>
        `
            : null
        }
      </div>
    `;
  }

  connectedCallback() {
    super.connectedCallback();
    const fieldValue = this.kasko.getState('input')[this.field_name];
    this.selectedValue =
      fieldValue !== undefined && fieldValue !== null && fieldValue !== ''
        ? this.field_values.filter((item) => fieldValue === item.value)[0]
        : {
            content_key: '',
            value: null,
          };
    this.disabled_static = this.kasko.evaluateJsonLogic(this.disabled?.schema || this.disabled);
    if (this.disabled_static && this.isOpened) {
      this.toggleDropdown();
    }
    this.kasko.addEventListener('field-validation', this.fieldValidationEvent, {
      name: this.field_name,
    });
    this.kasko.addEventListener('state-changed', this.onDisabledVarChanged, {
      slice: `input.${this.disabled_var}`,
    });
    this.addEventListener('disabled-changed', this.onDisabledChanged);
    window.addEventListener('click', this.onOutsideClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.kasko.removeEventListener('field-validation', this.fieldValidationEvent, {
      name: this.field_name,
    });
    this.kasko.removeEventListener('state-changed', this.onDisabledVarChanged, {
      slice: `input.${this.disabled_var}`,
    });
    this.removeEventListener('disabled-changed', this.onDisabledChanged);
    window.removeEventListener('click', this.onOutsideClick);
  }

  fieldValidation(data) {
    this.isError = !data;
  }

  onOutsideClick(e) {
    const withinBoundaries = e
      .composedPath()
      .includes(this.shadowRoot.getElementById(this.field_name)); // eslint-disable-line
    if (!withinBoundaries) {
      this.toggleDropdown(false);
    }
  }

  onDisabledChanged(event) {
    this.disabled_static = event.detail.disabled;
    if (this.disabled_static && this.isOpened) {
      this.toggleDropdown();
    }
  }

  onDisabledVarChanged() {
    if (this.disabled_var) {
      this.dispatchEvent(
        new CustomEvent('disabled-changed', {
          detail: {
            disabled: this.kasko.evaluateJsonLogic(this.disabled?.schema || this.disabled),
          },
        }),
      );
    }
  }

  toggleDropdown(isOpened) {
    if (this.disabled_static && !this.isOpened) {
      return;
    }
    const closestElement = this.closest(this.closest_element);
    if (closestElement) {
      if (!this.isOpened || isOpened === true) {
        closestElement.classList.add(this.closest_element_class);
      }

      if (this.isOpened || isOpened === false) {
        closestElement.classList.remove(this.closest_element_class);
      }
    }

    this.isOpened = typeof isOpened === 'undefined' ? !this.isOpened : isOpened;
  }

  selectItem(item) {
    this.selectedValue = { ...item };
    const selectedValue = {};
    selectedValue[this.field_name] = item.value;
    this.kasko.dispatchEvent('set-form-input', selectedValue);
    this.isOpened = false;
  }
}

export default CustomInputDropdownComponent;
